/**
 * Lobby waiting room (M12, FR-1.3/9.4) — a real pre-play roster for both modes.
 *
 * Shows every seated player with a presence marker (FR-2.7). In DYNAMIC mode it also
 * shows each player's submitted category wishes live from `state.lobby.preferences`
 * (public in the lobby — M12 §4.3), so the group sees the board taking shape before
 * generation runs. Age bands are never rendered per-player (the server strips them
 * from the wire — privacy, M12 §9).
 *
 * Read-only + role-agnostic: the host controls and the player preference input are
 * separate single-action components, so this stays a pure presentational roster.
 */

import type { BoardSource, LobbyState, Player } from '@jeopardy/shared';
import { Avatar } from './Avatar';

interface LobbyProps {
  players: Player[];
  mode: BoardSource;
  /** Present in LOBBY/GENERATING; category wishes keyed by playerId (dynamic mode). */
  lobby?: LobbyState;
}

export function Lobby({ players, mode, lobby }: LobbyProps): React.JSX.Element {
  const preferences = lobby?.preferences ?? {};
  return (
    <div className="lobby" data-testid="lobby">
      <h2 className="lobby__title">
        {mode === 'DYNAMIC' ? 'Lobby — waiting for players' : 'Lobby — ready to start'}
      </h2>
      {players.length === 0 ? (
        <p className="muted" data-testid="lobby-empty">
          Waiting for players to join…
        </p>
      ) : (
        <ul className="lobby__roster" data-testid="lobby-roster">
          {players.map((p) => {
            const wishes = preferences[p.id]?.categories ?? [];
            return (
              <li
                className="lobby__player"
                data-testid={`lobby-player-${p.id}`}
                key={p.id}
              >
                <Avatar name={p.name} avatarUrl={p.avatarUrl} />
                <span className="lobby__name">{p.name}</span>
                <span
                  className={`lobby__presence lobby__presence--${
                    p.connected ? 'online' : 'offline'
                  }`}
                  data-testid={`lobby-presence-${p.id}`}
                >
                  {p.connected ? 'online' : 'offline'}
                </span>
                {mode === 'DYNAMIC' &&
                  (wishes.length > 0 ? (
                    <ul className="lobby__wishes" data-testid={`lobby-wishes-${p.id}`}>
                      {wishes.map((c, i) => (
                        <li className="lobby__wish" key={`${c}-${i}`}>
                          {c}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <span className="muted" data-testid={`lobby-nowishes-${p.id}`}>
                      no categories yet
                    </span>
                  ))}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
